import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  IconButton,
  FormHelperText,
  Grid,
  TextField,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';

import { DialogWrapper } from '../DialogWrapper';
import { useEscKey } from '../../../hooks/useEscKey';

interface NewTicketDialogProps {
  open: boolean;
  onClose: () => void;
  newTicketForm: any;
  setNewTicketForm: (form: any) => void;
  mailContent: string;
  setMailContent: (content: string) => void;
  files: File[];
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>, existingFilesCount?: number) => void;
  onRemoveFile: (index: number) => void;
  onCreateTicket: () => Promise<void>;
  authUser: any;
  companies: any[];
  modules: any[];
  priorities: any[];
}

export const NewTicketDialog: React.FC<NewTicketDialogProps> = ({
  open,
  onClose,
  newTicketForm,
  setNewTicketForm,
  mailContent,
  setMailContent,
  files,
  onFileUpload,
  onRemoveFile,
  onCreateTicket,
  authUser,
  companies,
  modules,
  priorities
}) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  useEscKey(open, onClose);

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!newTicketForm.subject?.trim()) {
      newErrors.subject = 'Talep Konusu zorunludur';
    }
    if (!newTicketForm.module_id) {
      newErrors.module_id = 'Program seçimi zorunludur';
    }
    if (authUser?.role === 'admin' && !newTicketForm.company_id) {
      newErrors.company_id = 'Firma seçimi zorunludur';
    }
    if (newTicketForm.email && !/\S+@\S+\.\S+/.test(newTicketForm.email)) {
      newErrors.email = 'Geçerli bir e-posta adresi girin';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      await onCreateTicket();
    } catch (error) {
      console.error('Create ticket error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFileUpload(event);
    event.target.value = '';
  };

  const handleFormChange = (field: string, value: any) => {
    setNewTicketForm((prev: any) => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(ext || '')) return '🖼️';
    if (ext === 'pdf') return '📄';
    if (['doc', 'docx'].includes(ext || '')) return '📝';
    if (['xls', 'xlsx'].includes(ext || '')) return '📊';
    if (['zip', 'rar'].includes(ext || '')) return '📦';
    return '📎';
  };

  if (!open) {
    return null;
  }

  return (
    <DialogWrapper 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { 
          maxHeight: '95vh',
          marginLeft: '-30px',
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          width: '800px'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#2c3e50',
        color: 'white',
        py: 2,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
            📋 Yeni Talep Oluştur
          </Typography>
          <IconButton 
            onClick={onClose} 
            size="small" 
            sx={{ 
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {/* Firma - Sadece Admin */}
          {authUser?.role === 'admin' && (
            <FormControl fullWidth error={!!errors.company_id} size="small">
              <InputLabel sx={{ fontSize: '0.875rem' }}>Firma *</InputLabel>
              <Select
                value={newTicketForm.company_id || ''}
                label="Firma *"
                onChange={(e) => handleFormChange('company_id', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  },
                  '& .MuiSelect-select': {
                    padding: '10px 14px', // ✅ DİKEY ORTALAMA
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center'
                  }
                }}
              >
                <MenuItem value="" sx={{ fontSize: '0.875rem' }}>Seçiniz</MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id} sx={{ fontSize: '0.875rem' }}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.company_id && (
                <FormHelperText sx={{ fontSize: '0.75rem' }}>{errors.company_id}</FormHelperText>
              )}
            </FormControl>
          )}
          {/* Program */}
          <FormControl fullWidth error={!!errors.module_id} size="small">
            <InputLabel sx={{ fontSize: '0.875rem' }}>Program *</InputLabel>
            <Select
              value={newTicketForm.module_id || ''}
              label="Program *"
              onChange={(e) => handleFormChange('module_id', e.target.value)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e0e0e0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1976d2',
                },
                '& .MuiSelect-select': {
                  padding: '10px 14px', // ✅ DİKEY ORTALAMA
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center'
                }
              }}
            >
              <MenuItem value="" sx={{ fontSize: '0.875rem' }}>Seçiniz</MenuItem>
              {modules.map((module) => (
                <MenuItem key={module.id} value={module.id} sx={{ fontSize: '0.875rem' }}>
                  {module.name}
                </MenuItem>
              ))}
            </Select>
            {errors.module_id && (
              <FormHelperText sx={{ fontSize: '0.75rem' }}>{errors.module_id}</FormHelperText>
            )}
          </FormControl>
{authUser?.role === 'admin' && (
  <FormControl fullWidth size="small">
    <InputLabel sx={{ fontSize: '0.875rem' }}>Öncelik</InputLabel>
    <Select
      value={newTicketForm.priority_id || '1'} // Varsayılan Düşük Öncelik
      label="Öncelik"
      onChange={(e) => handleFormChange('priority_id', e.target.value)}
      sx={{
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#e0e0e0',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: '#1976d2',
        },
        '& .MuiSelect-select': {
          padding: '10px 14px',
          fontSize: '0.875rem',
          display: 'flex',
          alignItems: 'center'
        }
      }}
    >
      {priorities.map((priority) => (
        <MenuItem key={priority.id} value={priority.id} sx={{ fontSize: '0.875rem' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ fontSize: '14px' }}>
              {priority.id === 1 ? '🟢' : 
               priority.id === 2 ? '🔵' : 
               priority.id === 3 ? '🟠' : '🔴'}
            </span>
            <span>
              {priority.id === 1 ? 'Düşük' : 
               priority.id === 2 ? 'Orta' : 
               priority.id === 3 ? 'Yüksek' : 'Acil'}
            </span>
          </Box>
        </MenuItem>
      ))}
    </Select>
  </FormControl>
)}
          <Grid container spacing={2}>
            {/* E-posta */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="E-posta"
                value={newTicketForm.email || ''}
                onChange={(e) => handleFormChange('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                placeholder="musteri@firma.com"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem'
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                    padding: '10px 14px', // ✅ DİKEY ORTALAMA
                    height: '20px', // ✅ SABİT YÜKSEKLİK
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '0.75rem'
                  }
                }}
              />
            </Grid>

            {/* Talep Konusu */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Talep Konusu *"
                value={newTicketForm.subject || ''}
                onChange={(e) => handleFormChange('subject', e.target.value)}
                error={!!errors.subject}
                helperText={errors.subject}
                placeholder="Kısa ve net bir konu giriniz..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem'
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                    padding: '10px 14px', // ✅ DİKEY ORTALAMA
                    height: '20px', // ✅ SABİT YÜKSEKLİK
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '0.75rem'
                  }
                }}
              />
            </Grid>
          </Grid>

            {/* Talep Detayları */}
            <TextField
              fullWidth
              multiline
              rows={5}
              label="Talep Detayları"
              value={mailContent}
              onChange={(e) => setMailContent(e.target.value)}
              placeholder="Talep detaylarınızı buraya yazınız..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                  },
                  padding: '0', 
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem'
                },
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem',
                  padding: '12px 14px',
                  textAlign: 'left', 
                }
              }}
            />

            {/* Aksan Yazılım Tarafından Sorunun Açıklaması - Müşteri değilse */}
            {authUser?.role !== 'customer' && (
              <TextField
                fullWidth
                multiline
                rows={5}
                label="Aksan Yazılım Tarafından Sorunun Açıklaması"
                value={newTicketForm.description || ''}
                onChange={(e) => handleFormChange('description', e.target.value)}
                placeholder="Sorunu detaylı bir şekilde açıklayın..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                    padding: '0', 
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem'
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                    padding: '12px 14px',
                    textAlign: 'left', 
                  }
                }}
              />
            )}

          {/* Dosya Ekleme Bölümü */}
          <Box sx={{ 
            p: 2, 
            border: '1px dashed', 
            borderColor: 'grey.300', 
            borderRadius: 1,
            backgroundColor: '#fafafa'
          }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#333', fontSize: '0.9rem' }}>
              📎 Dosya Ekle (Opsiyonel)
            </Typography>

            <Button
              variant="outlined"
              component="label"
              startIcon={<AttachFileIcon />}
              size="small"
              disabled={files.length >= 10}
              sx={{ 
                mb: 2,
                borderColor: '#1976d2',
                color: '#1976d2',
                fontSize: '0.8rem',
                padding: '6px 12px',
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              DOSYA SEÇ
              <input
                type="file"
                hidden
                multiple
                onChange={handleFileSelect}
                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.txt,.csv"
              />
            </Button>

            <Typography variant="caption" display="block" color="text.secondary" sx={{ lineHeight: 1.4, fontSize: '0.75rem' }}>
              Maksimum 10 dosya, her dosya max 10MB. İzin verilen formatlar: 
              Resim (JPG, PNG, GIF), Döküman (PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX), 
              Arşiv (ZIP, RAR), Metin (TXT, CSV)
            </Typography>

            {/* Seçilen Dosyalar */}
            {files.length > 0 && (
              <Box sx={{ mt: 2 }}>
                {files.map((file, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      backgroundColor: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography sx={{ fontSize: '18px' }}>
                        {getFileIcon(file.name)}
                      </Typography>
                      <Box>
                        <Typography variant="body2" fontWeight="medium" sx={{ color: '#333', fontSize: '0.8rem' }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {formatFileSize(file.size)}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton 
                      size="small" 
                      onClick={() => onRemoveFile(index)}
                      color="error"
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(211, 47, 47, 0.04)'
                        },
                        padding: '4px'
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Chip 
                  label={`${files.length}/10 dosya seçildi`} 
                  color="primary" 
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1, fontWeight: 500, fontSize: '0.7rem', height: '24px' }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, gap: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          disabled={loading}
          sx={{ 
            minWidth: 100,
            borderColor: '#666',
            color: '#666',
            fontSize: '0.8rem',
            padding: '6px 16px',
            '&:hover': {
              borderColor: '#333',
              backgroundColor: 'rgba(0,0,0,0.04)'
            }
          }}
        >
          İPTAL
        </Button>
        <Button 
          onClick={handleCreate} 
          variant="contained" 
          disabled={loading}
          sx={{ 
            minWidth: 140,
            backgroundColor: '#2c3e50',
            fontSize: '0.8rem',
            padding: '6px 16px',
            '&:hover': {
              backgroundColor: '#1a252f'
            },
            '&:disabled': {
              backgroundColor: '#b0b0b0'
            }
          }}
        >
          {loading ? 'Oluşturuluyor...' : 'TALEP OLUŞTUR'}
        </Button>
      </DialogActions>
    </DialogWrapper>
  );
};